import { fileUrl } from '../lib/utils';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import {
    Receipt,
    Plus,
    Search,
    Filter,
    MoreHorizontal,
    Eye,
    CheckCircle,
    XCircle,
    Download,
    Euro,
    Loader2,
    Calendar,
    User,
} from 'lucide-react';
import { toast } from 'sonner';

const Expenses = () => {
    const { api, user, isAdmin, isCommercial } = useAuth();
    
    const [expenses, setExpenses] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        expense_date: new Date().toISOString().split('T')[0],
        category: '',
        amount_ttc: '',
        vat_amount: '',
        company_id: '',
        description: '',
        file: null,
    });

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter && statusFilter !== 'all') {
                params.append('status', statusFilter);
            }
            
            const [expensesRes, companiesRes] = await Promise.all([
                api.get(`/expenses?${params.toString()}`),
                api.get('/companies'),
            ]);
            
            setExpenses(expensesRes.data);
            setCompanies(companiesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        
        // Validate receipt file is present
        if (!formData.file) {
            toast.error('Le justificatif est obligatoire');
            return;
        }
        
        setSubmitting(true);
        
        try {
            const data = new FormData();
            data.append('expense_date', formData.expense_date);
            data.append('category', formData.category);
            data.append('amount_ttc', formData.amount_ttc);
            if (formData.vat_amount) data.append('vat_amount', formData.vat_amount);
            if (formData.company_id) data.append('company_id', formData.company_id);
            if (formData.description) data.append('description', formData.description);
            data.append('file', formData.file);
            
            await api.post('/expenses', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            
            toast.success('Note de frais créée');
            setShowCreateModal(false);
            resetForm();
            fetchData();
        } catch (error) {
            console.error('Failed to create expense:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusChange = async (expenseId, newStatus) => {
        try {
            await api.patch(`/expenses/${expenseId}`, { status: newStatus });
            toast.success('Statut mis à jour');
            fetchData();
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const resetForm = () => {
        setFormData({
            expense_date: new Date().toISOString().split('T')[0],
            category: '',
            amount_ttc: '',
            vat_amount: '',
            company_id: '',
            description: '',
            file: null,
        });
    };

    const getStatusBadge = (status) => {
        const styles = {
            draft: 'bg-gray-500/10 text-gray-500',
            submitted: 'bg-blue-500/10 text-blue-500',
            validated: 'bg-green-500/10 text-green-500',
            refused: 'bg-red-500/10 text-red-500',
            reimbursed: 'bg-emerald-500/10 text-emerald-500',
        };
        const labels = {
            draft: 'Brouillon',
            submitted: 'Soumise',
            validated: 'Validée',
            refused: 'Refusée',
            reimbursed: 'Remboursée',
        };
        return (
            <Badge className={styles[status] || styles.draft}>
                {labels[status] || status}
            </Badge>
        );
    };

    const categories = [
        { value: 'transport', label: 'Transport' },
        { value: 'repas', label: 'Repas' },
        { value: 'hebergement', label: 'Hébergement' },
        { value: 'fournitures', label: 'Fournitures' },
        { value: 'communication', label: 'Communication' },
        { value: 'autre', label: 'Autre' },
    ];

    const getCategoryLabel = (value) => {
        return categories.find(c => c.value === value)?.label || value;
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const filteredExpenses = expenses.filter(e => {
        if (!searchQuery) return true;
        return e.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               e.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               e.user_name?.toLowerCase().includes(searchQuery.toLowerCase());
    });

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading text-foreground">Notes de frais</h1>
                    <p className="text-muted-foreground mt-2 text-sm md:text-base">
                        Gérez vos notes de frais et remboursements
                    </p>
                </div>
                {isCommercial() && (
                    <Button
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="rounded-full"
                        data-testid="create-expense-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle note de frais
                    </Button>
                )}
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 rounded-lg border-border/40 focus:border-primary/50"
                                data-testid="expense-search-input"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-48 rounded-lg border-border/40" data-testid="expense-status-filter">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                <SelectItem value="submitted">Soumises</SelectItem>
                                <SelectItem value="validated">Validées</SelectItem>
                                <SelectItem value="refused">Refusées</SelectItem>
                                <SelectItem value="reimbursed">Remboursées</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {filteredExpenses.length === 0 ? (
                        <div className="text-center py-16">
                            <Receipt className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-40" />
                            <p className="text-muted-foreground">Aucune note de frais</p>
                            {isCommercial() && (
                                <Button
                                    variant="link"
                                    onClick={() => {
                                        resetForm();
                                        setShowCreateModal(true);
                                    }}
                                    className="mt-2 text-primary"
                                >
                                    Créer une note de frais
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b border-border/40">
                                    <TableHead className="text-muted-foreground font-semibold">Date</TableHead>
                                    <TableHead className="text-muted-foreground font-semibold">Catégorie</TableHead>
                                    <TableHead className="text-muted-foreground font-semibold">Montant</TableHead>
                                    <TableHead className="hidden md:table-cell text-muted-foreground font-semibold">Client</TableHead>
                                    {isAdmin() && <TableHead className="hidden lg:table-cell text-muted-foreground font-semibold">Commercial</TableHead>}
                                    <TableHead className="text-muted-foreground font-semibold">Statut</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredExpenses.map((expense) => (
                                    <TableRow key={expense.id} className="border-b border-border/40 hover:bg-muted/50 transition-colors" data-testid={`expense-row-${expense.id}`}>
                                        <TableCell className="py-4">
                                            <div className="flex items-center gap-2 text-foreground">
                                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                                {formatDate(expense.expense_date)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="py-4">
                                            <Badge variant="outline" className="border-border/40">
                                                {getCategoryLabel(expense.category)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="py-4">
                                            <p className="font-medium text-foreground">{formatCurrency(expense.amount_ttc)}</p>
                                            {expense.vat_amount && (
                                                <p className="text-sm text-muted-foreground">
                                                    TVA: {formatCurrency(expense.vat_amount)}
                                                </p>
                                            )}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell py-4 text-muted-foreground">
                                            {expense.company_name || '-'}
                                        </TableCell>
                                        {isAdmin() && (
                                            <TableCell className="hidden lg:table-cell py-4">
                                                <div className="flex items-center gap-2 text-muted-foreground">
                                                    <User className="w-4 h-4" />
                                                    {expense.user_name}
                                                </div>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {getStatusBadge(expense.status)}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="rounded-full">
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => {
                                                        setSelectedExpense(expense);
                                                        setShowDetailModal(true);
                                                    }}>
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        Voir détails
                                                    </DropdownMenuItem>
                                                    {expense.receipt_file_url && (
                                                        <DropdownMenuItem asChild>
                                                            <a href={fileUrl(expense.receipt_file_url)} target="_blank" rel="noopener noreferrer">
                                                                <Download className="w-4 h-4 mr-2" />
                                                                Télécharger justificatif
                                                            </a>
                                                        </DropdownMenuItem>
                                                    )}
                                                    {isAdmin() && expense.status === 'submitted' && (
                                                        <>
                                                            <DropdownMenuItem onClick={() => handleStatusChange(expense.id, 'validated')}>
                                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                                Valider
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => handleStatusChange(expense.id, 'refused')}>
                                                                <XCircle className="w-4 h-4 mr-2" />
                                                                Refuser
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                    {isAdmin() && expense.status === 'validated' && (
                                                        <DropdownMenuItem onClick={() => handleStatusChange(expense.id, 'reimbursed')}>
                                                            <Euro className="w-4 h-4 mr-2" />
                                                            Marquer remboursée
                                                        </DropdownMenuItem>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Nouvelle note de frais</DialogTitle>
                        <DialogDescription>
                            Ajoutez une dépense professionnelle
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Date *</Label>
                                <Input
                                    type="date"
                                    value={formData.expense_date}
                                    onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Catégorie *</Label>
                                <Select
                                    value={formData.category}
                                    onValueChange={(v) => setFormData({ ...formData, category: v })}
                                    required
                                >
                                    <SelectTrigger className="rounded-lg">
                                        <SelectValue placeholder="Sélectionner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map((cat) => (
                                            <SelectItem key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Montant TTC (€) *</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.amount_ttc}
                                    onChange={(e) => setFormData({ ...formData, amount_ttc: e.target.value })}
                                    required
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>TVA (€)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.vat_amount}
                                    onChange={(e) => setFormData({ ...formData, vat_amount: e.target.value })}
                                    className="rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Client associé</Label>
                            <SearchableCombobox
                                options={companies.map(c => ({ id: c.id, label: c.legal_name, sublabel: c.city || c.sector }))}
                                value={formData.company_id}
                                onValueChange={(v) => setFormData({ ...formData, company_id: v })}
                                placeholder="Rechercher un client..."
                                emptyMessage="Aucun client trouvé"
                                type="company"
                                data-testid="expense-company-select"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Justificatif *</Label>
                            <Input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                                className={`rounded-lg ${!formData.file ? 'border-orange-300' : ''}`}
                                required
                                data-testid="expense-receipt-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                Photo ou PDF du justificatif (obligatoire)
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                rows={3}
                                placeholder="Détails de la dépense..."
                                className="rounded-lg"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowCreateModal(false)}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Soumettre'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Détails de la note de frais</DialogTitle>
                    </DialogHeader>
                    {selectedExpense && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Date</Label>
                                    <p className="font-medium">{formatDate(selectedExpense.expense_date)}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Catégorie</Label>
                                    <p className="font-medium">{getCategoryLabel(selectedExpense.category)}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Montant TTC</Label>
                                    <p className="font-medium text-lg">{formatCurrency(selectedExpense.amount_ttc)}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">TVA</Label>
                                    <p className="font-medium">{formatCurrency(selectedExpense.vat_amount)}</p>
                                </div>
                            </div>
                            {selectedExpense.company_name && (
                                <div>
                                    <Label className="text-muted-foreground">Client</Label>
                                    <p className="font-medium">{selectedExpense.company_name}</p>
                                </div>
                            )}
                            {selectedExpense.description && (
                                <div>
                                    <Label className="text-muted-foreground">Description</Label>
                                    <p>{selectedExpense.description}</p>
                                </div>
                            )}
                            <div className="flex items-center gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Statut</Label>
                                    <div className="mt-1">{getStatusBadge(selectedExpense.status)}</div>
                                </div>
                                {selectedExpense.reviewed_by_name && (
                                    <div>
                                        <Label className="text-muted-foreground">Traité par</Label>
                                        <p className="font-medium">{selectedExpense.reviewed_by_name}</p>
                                    </div>
                                )}
                            </div>
                            {selectedExpense.receipt_file_url && (
                                <Button asChild variant="outline" className="w-full rounded-full">
                                    <a href={fileUrl(selectedExpense.receipt_file_url)} target="_blank" rel="noopener noreferrer">
                                        <Download className="w-4 h-4 mr-2" />
                                        Voir le justificatif
                                    </a>
                                </Button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Expenses;
